function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { COLORS, VARIANTS, SIZES, ARROWICON } from './constant';
import { getTextColor } from './helper';
import { Loader } from '../Loader';
import { Text } from '../Text';
import { validateString } from '../Utils/helper';
import styles from '@dtsl/css-naos-button/dist/index.module.css';
import ChevronDown from '@dtsl/icons/dist/icons/react/ChevronDown';
import ChevronUp from '@dtsl/icons/dist/icons/react/ChevronUp';
import ProgressiveIndicator from '@dtsl/icons/dist/icons/react/ProgressiveIndicator';

// eslint-disable-next-line react/display-name
const NaosButton = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    variant = VARIANTS.PRIMARY,
    color = COLORS.PRIMARY,
    iconAlone = false,
    loading = false,
    fullWidth = false,
    size = SIZES.MEDIUM,
    label = '',
    className = '',
    disabled = false,
    dataTestId = '',
    ariaLabel = 'button',
    icon = null,
    trailingIcon = null,
    labelClassName = '',
    ellipsis = false,
    arrowIcon = ARROWICON.DEFAULT,
    ...restprops
  } = props;
  const isColorlessVariant = [VARIANTS.SUBTLE, VARIANTS.AI].some(item => variant.includes(item));
  const naosBtnClass = classNames({
    [styles['naos-btn']]: true,
    [styles['naos-btn--loading']]: loading,
    [styles['naos-btn--full-width']]: fullWidth,
    [styles['naos-btn--subtle']]: variant.includes(VARIANTS.SUBTLE),
    [styles[`naos-btn--${variant}`]]: !!variant,
    [styles[`naos-btn--${color}`]]: !!color && !isColorlessVariant,
    [styles[`naos-btn--${size}`]]: !!size,
    [styles['naos-btn--icon-alone']]: iconAlone,
    [styles['naos-btn--nolabel']]: !label,
    [styles['naos-btn--dropdown-open']]: arrowIcon === ARROWICON.UP,
    [className]: !!className
  });
  const btnLabelClass = classNames({
    [styles['naos-btn__label']]: true,
    [styles['naos-btn__label--ellipsis']]: ellipsis,
    [styles['invisible']]: loading,
    [labelClassName]: !!labelClassName
  });

  // get button icon function and return icon component
  const getButtonIcon = buttonIcon => {
    if (buttonIcon && typeof buttonIcon === 'function') {
      return buttonIcon();
    }
    return null;
  };
  const DropdownIcon = arrowIcon === ARROWICON.UP ? ChevronUp : ChevronDown;

  // check if label is not alpha numeric then show aria-label
  const ariaText = iconAlone || validateString(label) ? ariaLabel : null;
  const textVariant = isColorlessVariant ? variant : `${color}-${variant}`;
  const invisible = loading ? {
    opacity: 0
  } : {};
  const isIconVisible = !iconAlone && (getButtonIcon(trailingIcon) || arrowIcon);
  const displayedIcon = arrowIcon ? /*#__PURE__*/React.createElement(DropdownIcon, _extends({
    "aria-hidden": "true"
  }, variant === 'ai-tertiary' ? {
    color: 'var(--sib-color_information-default)'
  } : {})) : getButtonIcon(trailingIcon);
  return /*#__PURE__*/React.createElement("button", _extends({}, restprops, {
    ref: ref,
    type: "button",
    className: naosBtnClass,
    "data-testid": dataTestId,
    "aria-label": ariaText,
    disabled: disabled || loading,
    "aria-disabled": disabled || null
  }), getButtonIcon(icon) ? /*#__PURE__*/React.createElement("span", {
    className: styles['naos-btn--icon-flex'],
    style: invisible
  }, getButtonIcon(icon)) : null, loading ? /*#__PURE__*/React.createElement(Loader, {
    isAbsolute: true,
    indicator: /*#__PURE__*/React.createElement(ProgressiveIndicator, {
      width: "24",
      height: "24"
    })
  }) : null, !iconAlone ? label && /*#__PURE__*/React.createElement(Text, {
    color: `var(--sib-color_${getTextColor(textVariant)})`,
    fontFamily: variant.includes(VARIANTS.SUBTLE) ? 'regular' : 'interactive',
    className: btnLabelClass,
    style: invisible
  }, label) : null, isIconVisible && /*#__PURE__*/React.createElement("span", {
    className: styles['naos-btn--icon-flex'],
    style: invisible,
    "aria-label": arrowIcon ? `${arrowIcon} arrow` : 'icon'
  }, displayedIcon));
});
NaosButton.propTypes = {
  size: PropTypes.oneOf(Object.values(SIZES)),
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  color: PropTypes.oneOf(Object.values(COLORS)),
  arrowIcon: PropTypes.oneOf(Object.values(ARROWICON)),
  dataTestId: PropTypes.string,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  iconAlone: PropTypes.bool,
  icon: PropTypes.func,
  trailingIcon: PropTypes.func,
  ellipsis: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  ariaLabel: function (props, propName) {
    if (props['iconAlone'] && typeof props[propName] !== 'string') {
      return new Error(`prop ${propName} type of string must be supplied when using iconAlone button.`);
    }
    if (props['iconAlone'] && !props[propName]) {
      return new Error(`prop ${propName} must be supplied of type string when using iconAlone variant of button component.`);
    }
  },
  onClick: PropTypes.func
};
export default NaosButton;